import { useMemo } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type { PrioritizationResult } from '@core-api/presentation/types/response';
import { NonInteractiveVisualizationWrapper } from '../../shared/components/NonInteractiveVisualizationWrapper';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { HundredPointsVisualization } from './HundredPointsVisualization';
import { ScreenReaderTable } from './ScreenReaderTable';
import type { HundredPointsChoice } from './types';

interface HundredPointsPresentationProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  shouldHideResults: boolean;
  choices: HundredPointsChoice[];
  shouldAnimate: boolean;
  results: PrioritizationResult[];
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
}

export function HundredPointsPresentation({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  choices,
  shouldAnimate,
  shouldHideResults,
  results,
  CanvasVisualizationArea,
}: HundredPointsPresentationProps) {
  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  const resultsWithFallback = useMemo(() => {
    const hasScores = results.some((d) => d.value > 0);
    const unsortedResults = choices.map((choice) => {
      if (shouldHideResults) {
        return {
          id: choice.id,
          label: choice.label,
          score: 0,
        };
      }

      const result = results.find(
        (result) => result.interactiveContentChoiceId === choice.id,
      );

      return {
        id: choice.id,
        label: choice.label,
        score: result ? Math.round(result.value) : 0,
      };
    });

    if (!hasScores) return unsortedResults;

    return unsortedResults.sort((a, b) => b.score - a.score);
  }, [choices, results, shouldHideResults]);

  return (
    <Box width="100%" height="100%" data-testid="100-points-container">
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />
      <ConditionalWrapper
        width="100%"
        height="100%"
        flex="1 1 auto"
        justifyContent="center"
        alignItems="center"
      >
        <HundredPointsVisualization
          shouldAnimate={shouldAnimate}
          results={resultsWithFallback}
          shouldHideResults={shouldHideResults}
          staticOptions={choices}
        />
        <ScreenReaderOnly>
          <ScreenReaderTable
            results={resultsWithFallback}
            shouldHideResults={shouldHideResults}
          />
        </ScreenReaderOnly>
      </ConditionalWrapper>
    </Box>
  );
}
